import isNull from 'lodash/isNull';
import isUndefined from 'lodash/isUndefined';

import { errorMessages } from '../../constants/utils/css';

// TODO: WEBCA-1805 establish conventions & update codebase to use v2 utilities
//
// v2 UTILITIES
type BemTypes = 'block' | 'el' | 'element' | 'mod' | 'modifier';
const BEM_CONFIG = {
  BLOCK: {
    REGEXP: /^([A-Z][a-z]+){1,}$/,
    DELIMITER: ' ',
  },
  ELEMENT: {
    REGEXP: /^[a-z]+([A-Z][a-z]+){1,}$/,
    DELIMITER: '__',
  },
  MODIFIER: {
    REGEXP: /^[a-z]+([A-Z][a-z]+){1,}$/,
    DELIMITER: '--',
  },
};

export const getValidBemClass = (className: string, bemType: BemTypes): string => {
  let regexp: RegExp | undefined = undefined;
  switch (bemType) {
    case 'block':
      regexp = BEM_CONFIG.BLOCK.REGEXP;
      break;
    case 'element':
    case 'el':
      regexp = BEM_CONFIG.ELEMENT.REGEXP;
      break;
    case 'modifier':
    case 'mod':
      regexp = BEM_CONFIG.MODIFIER.REGEXP;
      break;
  }

  if (regexp && className.match(regexp)) {
    return className;
  }

  let bemClass = className;

  // preserve title case
  bemClass = bemClass.replace(/([a-z]+)([A-Z])/g, '$1-$2').toLowerCase();

  // trim class
  bemClass = bemClass
    // remove leading unsupported characters
    .replace(/^[^a-z]*/, '')
    // remove trailing unsupported characters
    .replace(/[^a-z]*$/, '')
    // replace inline unsupported characters with temporary hyphens
    .replace(/[^a-z]+/g, '-');

  // convert to PascalCase
  if (bemType === 'block') {
    bemClass = bemClass.replace(/^(.)|[-](.)/g, (match: string) => match.toUpperCase());
    // convert to camelCase
  } else {
    bemClass = bemClass.replace(/[-](.)/g, (match: string) => match.toUpperCase());
  }

  // remove hyphens
  bemClass = bemClass.replace(/-/g, '');

  return bemClass;
};

export const createBEMv2 = (
  block: string,
  elementOrModifiers?: string | object,
  additionalClasses?: string | string[],
): string => {
  let classNames = [];
  const blockClass = getValidBemClass(block, 'block');

  // add base class
  if (isUndefined(elementOrModifiers) || isNull(elementOrModifiers)) {
    classNames.push(blockClass);

    // add element class (w/out modifiers)
  } else if (typeof elementOrModifiers === 'string') {
    if (elementOrModifiers !== '') {
      classNames.push(
        [blockClass, getValidBemClass(elementOrModifiers, 'element')].join(
          BEM_CONFIG.ELEMENT.DELIMITER,
        ),
      );
    }

    // add modifier classes
  } else if (typeof elementOrModifiers === 'object') {
    classNames.push(blockClass);
    for (const mod in elementOrModifiers) {
      // skip if no valid modifier value
      if (!elementOrModifiers[mod as keyof typeof elementOrModifiers]) {
        continue;
      }

      // get value based on boolean or string
      let modValue;
      if (typeof elementOrModifiers[mod as keyof typeof elementOrModifiers] === 'boolean') {
        modValue = mod;
      } else {
        modValue = [elementOrModifiers[mod as keyof typeof elementOrModifiers], mod].join('-');
      }
      modValue = getValidBemClass(modValue, 'modifier');

      if (elementOrModifiers[mod as keyof typeof elementOrModifiers]) {
        classNames.push([blockClass, modValue].join(BEM_CONFIG.MODIFIER.DELIMITER));
      }
    }
  }

  // add optional additional classes
  if (additionalClasses?.length) {
    classNames = classNames.concat(additionalClasses);
  }

  return classNames.join(BEM_CONFIG.BLOCK.DELIMITER);
};

// v1 UTILITIES
//

/**
 * May throw an error
 *
 * @param  {string} type - Description of str
 * @param  {string} str - What string passed
 * @param  {Regex} regex - What is the regex
 * @param  {boolean} hasNoDashCheck - Do we check for hasNoDash
 * @param  {boolean} hasNoUnderscoreCheck - Do we check for hasNoUnderscore
 * @returns {boolean} - Is it valid?
 *
 * example:
 *   call: isValidBEM('baseClass', 'hi-', false, false, true);
 *   console.error: "Invalid baseClass: 'hi-' * Failed regex check: /^[A-Z].* / * Should NOT include any dash.
 *   result: false
 *   call: isValidBEM('baseClass', 'Hi', true, true, true);
 *   result: true
 */
function isValidBEM(
  type: string,
  str: string,
  regex: RegExp,
  hasNoDashCheck: boolean,
  hasNoUnderscoreCheck: boolean,
): boolean {
  const isValidRegex = regex.test(str);
  const hasNoDash = hasNoDashCheck ? !str.includes('-') : true;
  const hasNoUnderscore = hasNoUnderscoreCheck ? !str.includes('_') : true;
  const isValid = isValidRegex && hasNoDash && hasNoUnderscore;
  let errorMessage = `Invalid ${type}: '${str}'`;
  if (!isValidRegex) {
    errorMessage += `${errorMessages.regex.defaultMessage}: ${regex}`;
  }
  if (!hasNoDash) {
    errorMessage += errorMessages.dash.defaultMessage;
  }
  if (!hasNoUnderscore) {
    errorMessage += errorMessages.underscore.defaultMessage;
  }
  if (!isValid) {
    // eslint-disable-next-line no-console
    console.error(errorMessage);
  }
  return isValid;
}

/**
 * Check if string starts with a11y (accessibility specific)
 *
 * @param  {string} str - a string
 * @returns {boolean} - does it start with `a11y`
 *
 * example:
 *   call: startsWithA11y('a11y--hello')
 *   result: true
 *   call: startsWithA11y('hello')
 *   result: false
 */
function startsWithA11y(str: string): boolean {
  const a11yRegex = /^a11y/;
  return a11yRegex.test(str);
}

/**
 * Check if valid a11y modifier
 * Rules:
 * - Must follow 'a11y--someMod'
 *
 * @param  {string} modifier - createBEM's baseClass
 * @returns {boolean} - isValidRegex
 *
 * example:
 *   call: isValidA11y('a11y--hello')
 *   result: true
 *   call: isValidA11y('a11y--')
 *   result: false
 *   call: isValidA11y('a11y--hello--')
 *   result: false
 */
function isValidA11yModifier(modifier: string): boolean {
  const a11yRegex = /^a11y--[a-z]+[a-zA-Z]*$/;
  return isValidBEM('a11y modifier', modifier, a11yRegex, false, false);
}

/**
 * Check if valid baseClass
 * Rules:
 * - Must start with upper case letter
 * - Must not include any dash
 *
 * @param  {string} baseClass - createBEM's baseClass
 * @returns {boolean} - sisValidRegex && hasNoDash
 *
 * example:
 *   call: isValidBaseClass('BaseClass')
 *   result: true
 *   call: isValidBaseClass('BaseClass-')
 *   result: false
 *   call: isValidBaseClass('baseClass')
 *   result: false
 */
function isValidBaseClass(baseClass: string): boolean {
  const startsWithUpperCaseRegex = /^[A-Z].*/;
  return isValidBEM('baseClass', baseClass, startsWithUpperCaseRegex, true, false);
}

/**
 * Check if valid modifier
 * Rules:
 * - Must start with lower case letter
 * - Must not include any dash
 * - Must not include any underscore
 *
 * @param  {string} modifier - one of createBEM's modifiers
 * @returns {boolean} - isValidRegex && hasNoDash && hasNoUnderscore
 *
 * example:
 *   call: isValidModifier('modifier')
 *   result: true
 *   call: isValidModifier('modifier-')
 *   result: false
 *   call: isValidModifier('Modifier')
 *   result: false
 *   call: isValidModifier('modifier_')
 *   result: false
 */
function isValidModifier(modifier: string): boolean {
  const startsWithLowerCaseRegex = /^[a-z].*/;
  return isValidBEM('modifier', modifier, startsWithLowerCaseRegex, true, true);
}

/**
 * BEM switch
 *
 * @param {string} baseClass - a string
 * @param {string} modifier - string that may be combined with baseClass
 * @returns {string} - modified baseClass
 * Will not return anything if not baseClass or modifier is not valid
 *
 * example:
 *   call: getBEMClass('BaseClass', 'hello')
 *   result: BaseClass--hello
 *   call: getBEMClass('BaseClass', 'a11y--hello')
 *   result: a11y--hello
 *   call: getBEMClass('BaseClass', 'a11y--hello')
 *   result: a11y--hello
 *   call: getBEMClass('BaseClass', 'NotValidModifier')
 *   result: nothing
 */
function getBEMClass(baseClass: string, modifier: string): string | null {
  if (startsWithA11y(modifier) && isValidA11yModifier(modifier)) {
    return modifier;
  }
  if (!startsWithA11y(modifier) && isValidModifier(modifier)) {
    return `${baseClass}--${modifier}`;
  }
  return null;
}

/**
 * Spiritual Successor to jeCSS (A Jesse Dawson solution to pass CSS styles from a parent component to a child component.)
 * May we mourn its passing.
 *
 * @param {string} baseClass - name-spaced class name.
 * @param {object} modifiers - potential modifier class strings.
 * @returns {string} properly name-spaced class names.
 *
 * sidenote: anything with starting with 'a11y' will not have baseClass prepended to it
 *
 * example:
 *   call: createBEM('Foo', { light: true, nowPlaying: false, 'a11y--visuallyHidden': true })
 *   result: 'Foo Foo--light a11y--visuallyHidden'
 */

export function createBEM(baseClass: string, modifiers: Record<string, boolean>): string {
  const BEM: string[] = [];

  if (isValidBaseClass(baseClass)) {
    BEM.push(baseClass);

    Object.keys(modifiers).forEach(key => {
      if (modifiers[key]) {
        const BEMClass = getBEMClass(baseClass, key);
        BEMClass ? BEM.push(BEMClass) : null;
      }
    });
  }
  return BEM.join(' ');
}
